import TagManager from 'react-gtm-module'
import { telemetryStorage } from 'app/modules/auth/helper/storage'
import type { GTMDataLayerEvent, GTMConsentEvent, GTMPageViewEvent } from '../types/gtm'
import type { FilescanInteractionName, FilescanInteractionConfig, ClickEventParams } from '../types/telemetry'
import { FilescanInteraction, AmplitudeEvent } from '../types/telemetry'
import { isInIframe } from './functions'
import { logger } from 'app/components'

/**
 * Check if GTM has been initialized
 * @returns {boolean} True if GTM script is loaded and dataLayer exists
 */
export function isGTMInitialized(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const hasDataLayer = window.dataLayer !== undefined && Array.isArray(window.dataLayer)
  const hasGTMStartEvent = hasDataLayer && window.dataLayer.some((item: any) => item.event === 'gtm.js')

  return hasGTMStartEvent
}

/**
 * Push event to GTM dataLayer
 * @param event Event data to push to dataLayer
 */
export function trackEvent(event: GTMDataLayerEvent): void {
  const init = isGTMInitialized()

  if (init) {
    TagManager.dataLayer({
      dataLayer: event,
    })
  }
}

/**
 * Push analytics consent status to dataLayer
 * @param granted Whether consent is granted or denied
 */
export function pushAnalyticsConsent(granted: boolean): void {
  const event: GTMConsentEvent = {
    event: granted ? 'analytics_consent_granted' : 'analytics_consent_revoked',
    consent_status: granted ? 'granted' : 'denied',
  }
  trackEvent(event)
}

/**
 * Extract domain from URL
 * @param url Full URL
 */
function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url)
    return urlObj.hostname
  } catch {
    return ''
  }
}

/**
 * Push page view event to dataLayer
 * @param routeTemplate Optional route template (e.g., /scan/:id)
 * @param userId Optional user ID if authenticated
 */
export function pushPageView(routeTemplate?: string, userId?: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const fullUrl = window.location.href
  const pathname = window.location.pathname
  const hostname = window.location.hostname
  const title = document.title
  const referrer = document.referrer || undefined
  const referringDomain = referrer ? extractDomain(referrer) : undefined

  const event: GTMPageViewEvent = {
    event: 'page_view',
    page_url: fullUrl,
    page_path: pathname,
    page_title: title,
    page_hostname: hostname,
  }

  if (routeTemplate) {
    event.route_template = routeTemplate
  }

  if (referrer) {
    event.referrer = referrer
  }

  if (referringDomain) {
    event.referring_domain = referringDomain
  }

  if (userId !== undefined && userId !== null) {
    event.user_id = userId
  }

  trackEvent(event)
}

/**
 * Push one-time product pageview event in community
 * This event fires only once per session to indicate product usage
 */
export function pushGtmProductInteraction(): void {
  if (typeof window === 'undefined' || telemetryStorage.isProductPageviewFired()) {
    return
  }

  trackEvent({
    event: 'product_pageview',
  })

  telemetryStorage.markProductPageviewFired()
}

/**
 * Post one-time product interaction event to parent window for iframed version,
 * when the application is running  - typically - on myopswat portal.
 * This event fires only once per session to indicate product usage.
 * @param targetOrigin The origin to post message to (e.g., myopswat portal url)
 */
export function postIframeProductInteraction(targetOrigin: string): void {
  if (!isInIframe() || telemetryStorage.isProductInteractionFired()) {
    return
  }

  window.parent.postMessage(
    {
      event: 'product_interaction',
      product_name: 'filescan',
    },
    targetOrigin
  )

  telemetryStorage.markProductInteractionFired()
}

/**
 * Map of Filescan interactions to their conversion metadata
 */
const filescanInteractionConfig: Record<FilescanInteractionName, FilescanInteractionConfig> = {
  [FilescanInteraction.SimilaritySearchFilter]: {
    amplitude_event: AmplitudeEvent.CTAClicked,
    conversion_category: 'filter',
    conversion_type: 'similarity_search',
  },
  [FilescanInteraction.GenerateIOCQuery]: {
    amplitude_event: AmplitudeEvent.CTAClicked,
    conversion_category: 'query',
    conversion_type: 'generate_ioc',
  },
  [FilescanInteraction.DownloadReport]: {
    amplitude_event: AmplitudeEvent.AssetDownloaded,
    conversion_category: 'download',
    conversion_type: 'report_download',
    asset_type: 'report',
  },
  [FilescanInteraction.DownloadIOCS]: {
    amplitude_event: AmplitudeEvent.AssetDownloaded,
    conversion_category: 'download',
    conversion_type: 'ioc_download',
    asset_type: 'iocs',
  },
  [FilescanInteraction.ExtractedFilesDownload]: {
    amplitude_event: AmplitudeEvent.AssetDownloaded,
    conversion_category: 'download',
    conversion_type: 'extracted_file_download',
    asset_type: 'extracted_file',
  },
}

/**
 * Push a Filescan interaction click event to GTM dataLayer
 * @param params Click event parameters
 */
export function pushClickEvent(params: ClickEventParams): void {
  const { interaction, id, data } = params

  if (!interaction) {
    return
  }

  const config = filescanInteractionConfig[interaction]
  if (!config) {
    logger.warn(`No telemetry config found for interaction: ${interaction}`)
    return
  }

  const event: GTMDataLayerEvent = {
    event: interaction,
    amplitude_event: config.amplitude_event,
    conversion_category: config.conversion_category,
    conversion_type: config.conversion_type,
  }

  if (id) {
    event.element_id = id
  }

  if (config.asset_type) {
    event.asset_type = config.asset_type
  }

  if (data) {
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        event[key] = value
      }
    })
  }

  trackEvent(event)
}
